import { useEffect, useState } from "react";
import { Modal, Button, Label, TextInput, Pagination, Tooltip, Card } from "flowbite-react";
import {  HiDownload,  HiPencil, HiCheck, HiTrash, HiOutlineClipboardCheck } from "react-icons/hi";
import useFiles from "../hooks/useFiles";
import { Attachment, AttachmentType, AttachmentUpload } from "../types/attachment";
import { toast } from 'react-toastify';
import { useForm } from "react-hook-form";
import styled from "styled-components";
import useDebounce from "../hooks/useDebounce";
import { useAuth } from "../context/authContext";

interface FileListProps {
    type: string;
    page: number;
    onSelect: (file:Attachment) => void;
  }
  
  const FilesContainer = styled.div`
    display:flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding: 0 16px;
    justify-content: space-between;
  
    .file-item {
      width: calc(25% - 8px); /* 25% width for each item with 8px padding */
      margin-bottom: 16px;
    }  
  `
  
  const Image = styled.div<{img: string}>`
    background-image: url(${({ img}) => img});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
    width: 100%;
    height: 180px;
  `

const FileList = (props: FileListProps) => {
    const { permissions } = useAuth()
  
    const canDelete = permissions.find(item => item.slug === 'borrar-archivos')
  
    const [files, setFiles] = useState<Array<Attachment>| undefined>([]);
    const [totalFiles, setTotalFiles] = useState<number>(0)
    const [page, setPage] = useState<number>(props.page)
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [isUpdatingFile, setIsUpdatingFile] = useState<boolean>(false);
    const [fileToUpdate, setFileToUpdate] = useState<Attachment | null>(null);
    const [isSubmittingUpdatedFile, setIsSubmittingUpdatedFile] = useState<boolean>(false);
    const debounceSearchTerm = useDebounce(searchTerm, 500)
  
    const {getFiles, deleteFile, updateFile} = useFiles()
  
    const {register, handleSubmit, setValue} = useForm()
  
  
    const performSearch = async () => {
      if(debounceSearchTerm.length === 0){
         await fetchFiles()
         return
      }
       
      const files = await getFiles({
        type: props.type,
        title: searchTerm,
        source: searchTerm,
        foot: searchTerm,
        page
      });
      if(files.error){
        toast("No se pudieron obtener los archivos",{
          type:"error"
        })
        return
      }
      setFiles(files.files);
      setTotalFiles(files.totalItems || 0)
    }
  
    useEffect(() => {
      (async () => {
        await performSearch();
      })()
      // eslint-disable-next-line
    },[debounceSearchTerm])
  
    const fetchFiles = async () => {
      const files = await getFiles({
        type: props.type, 
        page
      });
      if(files.error){
        toast("No se pudieron obtener los archivos",{
          type:"error"
        })
        return
      }
      setFiles(files.files);
      setTotalFiles(files.totalItems || 0)
    };
  
    const onSubmitUpdatedFile = async (data: any) => {
      if(!fileToUpdate) return
  
      setIsSubmittingUpdatedFile(true)
  
      const fileData: AttachmentUpload = {
        id: fileToUpdate.id,
        type: fileToUpdate.type.toUpperCase() as AttachmentType,
        title: data.title,
        foot: data.foot,
        source: data.author,
      }
  
      const isUpdated = await updateFile(fileData)
      toast(isUpdated ? "Archivo actualizado correctamente" : "Ocurrió un error al actualizar el archivo", {
        type: isUpdated ? "success" : "error"
      })
      setIsUpdatingFile(isUpdated ? false : true)
      setFileToUpdate(isUpdated ? null : fileToUpdate)
      setIsSubmittingUpdatedFile(false)
      await performSearch()
    }
  
    const onUpdateFile = (file: Attachment) => {
      setFileToUpdate(file);
      setIsUpdatingFile(true);
  
      setValue("title", file.title)
      setValue("author", file.source)
      setValue("foot", file.foot)
    }
  
    const onDeleteFile = async (fileId: number) => {
      if(!fileId || !canDelete) return
  
      const confirm = window.confirm('¿Deseas eliminar este archivo? Esta acción no se puede deshacer.')
      if(!confirm) return
  
      const deleted = await deleteFile(fileId)
      toast(deleted ? "Archivo eliminado correctamente" : "Error al eliminar archivo", {
        type: deleted ? "success" : "error"
      })
  
      if(deleted){
        await fetchFiles()
      }
    }
  
    const handleSearch = (e:any) => {
      const value = e.target.value
      setSearchTerm(value)
    }
  
    const onPageChange = (page: number) => setPage(page)
  
      useEffect(() => {
          fetchFiles();
          // eslint-disable-next-line
      }, [page]);
  
      const renderButtonGroup = (file: Attachment) => {
        return <div className="flex items-center gap-2 justify-center">
          <Tooltip content="Seleccionar">
            <Button size={"xs"} color={"gray"}  onClick={() => props.onSelect(file)}>
              <HiCheck  className="h-5 w-5" />
            </Button>
          </Tooltip>
          {canDelete && <Tooltip content="Eliminar">
            <Button size={"xs"} color={"gray"}  onClick={() => onDeleteFile(file.id)}>
              <HiTrash  className="h-5 w-5" />
            </Button>
          </Tooltip>}
          <Tooltip content="Editar">
            <Button size={"xs"} color={"gray"}  onClick={() => onUpdateFile(file)}>
              <HiPencil  className="h-5 w-5" />
            </Button>
          </Tooltip>
          <Tooltip content="Descargar">
            <Button size={"xs"} color={"gray"} onClick={() => window.open(`${process.env.REACT_APP_UPLOADS}/${file.url}`)}>
              <HiDownload  className="h-5 w-5" />
            </Button>
          </Tooltip>
        </div>
      }
  
      const renderFiles = () => {
        if(files?.length === 0){
          return <Card className="w-full">
            <p>No se encontraron archivos</p>
          </Card>
        }
  
        if(!files){
          return <span>Espere ...</span>
        }
  
        if(props.type === "image"){
          return files.map(item => <Image key={item.id} className="relative file-item flex rounded-lg border border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-gray-800 flex-col" img={`${process.env.REACT_APP_UPLOADS}/${item.url}`}>
            <div style={{ position: "absolute", left: 10, right: 10, bottom: 10, }}>
            {renderButtonGroup(item)}
            </div>
          </Image>)
        }
  
        if(props.type === "video"){
          return files.map(item => <div key={item.id} className="relative file-item flex rounded-lg border border-gray-200 bg-white p-2 items-center shadow-md dark:border-gray-700 dark:bg-gray-800 flex-col">
            <video src={`${process.env.REACT_APP_UPLOADS}/${item.url}`} controls></video>
            {renderButtonGroup(item)}
          </div>)
        }
  
        if(props.type === "pdf"){
          return files.map(item => <div key={item.id} className="relative file-item flex rounded-lg border border-gray-200 bg-white p-2 items-center shadow-md dark:border-gray-700 dark:bg-gray-800 flex-col">
              <HiOutlineClipboardCheck size={24} />
              <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                  {item.title}
              </p>
              {renderButtonGroup(item)}
          </div>)
        }
      }
  
      return (
        <>
          <div className="pl-4 pr-4 pt-0 pb-2">
            <TextInput placeholder="Buscar archivo ..." onChange={handleSearch} />
          </div>
          <FilesContainer>
              {renderFiles()}
              <div className="flex justify-center overflow-x-auto sm:justify-center">
                <Pagination currentPage={page || 1} totalPages={Math.ceil(totalFiles / 20)} onPageChange={onPageChange} />
              </div>
          </FilesContainer>
          
  
          {isUpdatingFile && fileToUpdate &&<Modal size="5xl" show={isUpdatingFile} onClose={() => setIsUpdatingFile(false)}>
              <Modal.Header>Actualizar detalles de archivo</Modal.Header>
              <Modal.Body className="p-0">
                <div className="flex w-full items-center justify-center p-6">
                    <div className="flex w-full flex-col gap-4">
                        <div className="mb-2 block">                
                            {fileToUpdate.type === "image" && <img src={`${process.env.REACT_APP_UPLOADS}/${fileToUpdate.url.replace("../uploads/","")}`} width={250} height={250} alt="preview" />}
                        </div>
                        <div>
                            <div className="mb-2 block">
                            <Label htmlFor="title" value="Título" />
                            </div>
                            <TextInput {...register("title")} id="title" type="text" />
                        </div>
                        <div>
                            <div className="mb-2 block">
                            <Label htmlFor="author" value="Autor" />
                            </div>
                            <TextInput {...register("author")} id="author" type="text" />
                        </div>
                        <div>
                            <div className="mb-2 block">
                            <Label htmlFor="author" value="Pié de archivo" />
                            </div>
                            <TextInput {...register("foot")} id="foot" type="text" />
                        </div>
                    </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
              <Button onClick={handleSubmit(onSubmitUpdatedFile)} disabled={isSubmittingUpdatedFile}>{ isSubmittingUpdatedFile ? 'Espere ...' : 'Guardar' }</Button>
                  <Button color="gray" disabled={isSubmittingUpdatedFile} onClick={() => setIsUpdatingFile(false)}>Cancelar</Button>
              </Modal.Footer>
          </Modal>}
        </>
      );
  }

  export default FileList;